import React from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Typography, Paper } from "@mui/material";
import { Lock as LockIcon } from "@mui/icons-material";
import { isAdmin, hasAnyRole } from "../utils/roleUtils";

/**
 * Protected Route component that checks user role
 * Redirects to appropriate page if user doesn't have required role
 */
const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requiredRoles = [],
  fallbackPath = "/dashboard",
  showAccessDenied = true,
}) => {
  const { user, isAuthenticated } = useSelector((state) => state.user);

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/auth" replace />;
  }

  // Check for admin requirement
  if (requireAdmin && !isAdmin(user)) {
    return showAccessDenied ? (
      <AccessDenied
        message="This page is only accessible to Administrators."
        redirectPath={fallbackPath}
      />
    ) : (
      <Navigate to={fallbackPath} replace />
    );
  }

  // Check for specific roles
  if (requiredRoles.length > 0 && !hasAnyRole(user, requiredRoles)) {
    return showAccessDenied ? (
      <AccessDenied
        message="You don't have the required permissions to access this page."
        redirectPath={fallbackPath}
      />
    ) : (
      <Navigate to={fallbackPath} replace />
    );
  }

  // User has required permissions, render children
  return <>{children}</>;
};

/**
 * Access Denied component
 */
const AccessDenied = ({ message, redirectPath }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: "center",
          maxWidth: 500,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "error.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: "error.main" }} />
        </Box>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {message || "You don't have permission to access this page."}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please contact your administrator if you believe this is an error.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ProtectedRoute;
